import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Zoom,
  Typography,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ResponsiveDialog from 'components/ResponsiveDialog'
import FileUpload from 'components/FileUpload'
import VirtualizedList from 'components/VirtualizedList'
import Button from 'components/Button'
import TextField from 'components/TextField'
import Condition from 'components/Condition'
import ConditionSwitch from 'components/ConditionSwitch'
import { isMobile } from 'services/browserAgent'
import { Check, Info, Search } from '@mui/icons-material'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { ChatThreadEditDialog_SearchQuery } from '__generated__/ChatThreadEditDialog_SearchQuery.graphql'
import { ChatThreadEditDialog_Thread$key } from '__generated__/ChatThreadEditDialog_Thread.graphql'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useChat } from 'hooks/chat'
import { useSearch } from 'hooks/search'
import { ChatThreadEditDialog_CreateMutation } from '__generated__/ChatThreadEditDialog_CreateMutation.graphql'
import { ChatThreadEditDialog_UpdateMutation } from '__generated__/ChatThreadEditDialog_UpdateMutation.graphql'
import { ChatThreadEditDialog_Recipient$key } from '__generated__/ChatThreadEditDialog_Recipient.graphql'
import { makeStyles } from 'tss-react/mui'
import Contact from '../Contact'

const useStyles = makeStyles()(theme => ({
  avatarField: {
    marginBottom: theme.spacing(4),
    '& > div': {
      alignItems: 'center !important',
    },
  },
}))

interface IChatThreadEditDialogProps {
  onClose: () => void
  threadFragmentRef?: ChatThreadEditDialog_Thread$key
  recipientFragmentRef?: ChatThreadEditDialog_Recipient$key
}

function ChatThreadEditDialog({ onClose, threadFragmentRef, recipientFragmentRef }: IChatThreadEditDialogProps) {
  const [selectedUsersIds, setSelectedUsersIds] = useState<string[]>([])
  const [nameFilter, setNameFilter] = useState('')
  const [title, setTitle] = useState('')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const { setConversation } = useChat()
  const { classes } = useStyles()
  const theme = useTheme()

  const thread = useFragment(graphql`
    fragment ChatThreadEditDialog_Thread on ChatThread {
      id
      title
      avatarUrl
      purpose
      
      recipient {
        ...ChatThreadEditDialog_Recipient
      }
      
      users {
        id
      }
    }
  `, threadFragmentRef)

  const preselectedContact = useFragment(graphql`
    fragment ChatThreadEditDialog_Recipient on Recipient {
      ... on User {
        id
        name
      }
      
      ... on ChatBot {
        id
        name
      }
    }
  `, (recipientFragmentRef || thread?.recipient) as ChatThreadEditDialog_Recipient$key)

  useEffect(() => {
    let list = thread?.users?.map(u => u.id) || []
    if (preselectedContact?.id && !list.includes(preselectedContact.id)) list.push(preselectedContact.id)
    setSelectedUsersIds(list)
    setTitle(thread?.title || '')
    setAvatarUrl(thread?.avatarUrl || null)
  }, [thread, preselectedContact])

  const dialogTitle = useMemo(() => {
    let value = thread ? 'Edit Chat' : 'Create New Group Chat'

    if (!thread && preselectedContact?.name) value = `Create new Group Chat With ${preselectedContact.name}`

    return value
  }, [preselectedContact, thread])

  const toggleUser = useCallback((id: string) => {
    if (selectedUsersIds.find(u => u === id)) {
      setSelectedUsersIds(selectedUsersIds.filter(u => u !== id))
    } else {
      setSelectedUsersIds(selectedUsersIds.concat(id))
    }
  }, [selectedUsersIds])

  const { execute: createThread, loading: createLoading } = useQuickCommit<ChatThreadEditDialog_CreateMutation>(graphql`
    mutation ChatThreadEditDialog_CreateMutation($input: CreateChatThreadInput!) {
      createChatThread(input: $input) {
        chatThread {
          id

          ...ChatDialogContent_Thread
          
          # TODO: refresh the conversations list here
        }
      }
    }
  `)

  const { execute: updateThread, loading: updateLoading } = useQuickCommit<ChatThreadEditDialog_UpdateMutation>(graphql`
    mutation ChatThreadEditDialog_UpdateMutation($input: UpdateChatThreadInput!) {
      updateChatThread(input: $input) {
        chatThread {
          ...ChatDialogContent_Thread
        }
      }
    }
  `)

  const save = useCallback(async () => {
    if (thread?.id) {
      await updateThread({
        input: {
          chatThreadId: thread.id,
          userIds: selectedUsersIds,
          title,
          avatarUrl,
        },
      })
    } else {
      const result = await createThread({
        input: {
          userIds: selectedUsersIds,
          title,
          avatarUrl,
        },
      })
      const newThreadId = result?.createChatThread?.chatThread?.id
      if (newThreadId) {
        setConversation({ id: newThreadId })
      }
    }

    onClose()
  }, [selectedUsersIds, title, avatarUrl, thread])

  const updateNameFilter = useCallback((event: any) => { setNameFilter(event.target.value) }, [setNameFilter])
  const updateTitle = useCallback((event: any) => { setTitle(event.target.value) }, [setTitle])
  const updateAvatar = useCallback((value: any) => { setAvatarUrl(value) }, [setAvatarUrl])

  const { results: list, loading: searching } = useSearch<
    ChatThreadEditDialog_SearchQuery,
    NonNullable<NonNullable<NonNullable<NonNullable<ChatThreadEditDialog_SearchQuery['response']['currentUser']>['contacts']>['nodes']>[number]>
  >(graphql`
    query ChatThreadEditDialog_SearchQuery($search: String, $threadId: ID, $hasThread: Boolean!) {
      currentUser @skip(if: $hasThread) {
        contacts(first: 3, filters: { search: $search }) {
          nodes {
            id
            
            ...Contact_Contact
          }
        }
      }

      thread: optionalNode(id: $threadId) @include(if: $hasThread) {
        ... on ChatThread {
          inviteableAndCurrentUsers(first: 3, filters: { search: $search }) {
            nodes {
              id
              
              ...Contact_Contact
            }
          }
        }
      }
    }
  `, {
    search: nameFilter,
    threadId: thread?.id,
    hasThread: Boolean(thread?.id),
  }, data => data?.thread?.inviteableAndCurrentUsers?.nodes || data?.currentUser?.contacts?.nodes)

  const disabled = selectedUsersIds.length < 2 || createLoading || updateLoading || searching || false
  const disableTitleAndAvatarEdit = thread?.purpose === 'applicant_feedback'

  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {dialogTitle}
      </DialogTitle>

      <DialogContent>
        <Condition condition={!disableTitleAndAvatarEdit}>
          <div className={classes.avatarField}>
            <FileUpload
              avatar
              label={avatarUrl ? 'Update Image' : 'Add Image'}
              input={{
                onChange: updateAvatar,
                value: avatarUrl,
                name: 'avatar_url',
              }}
            />
          </div>

          <TextField
            label="Title"
            name="title"
            value={title}
            onChange={updateTitle}
            helperText="You can leave an empty title and an automatically generated title will show instead."
            fullWidth
          />

          <Typography variant="h6" style={{ marginTop: theme.spacing(4) }}>
            <ConditionSwitch>
              <Condition condition={thread?.purpose === 'applicant_feedback'}>
                Reviewers
              </Condition>
              <Condition condition>
                Participants
              </Condition>
            </ConditionSwitch>
          </Typography>
        </Condition>

        <DialogContentText>
          <ConditionSwitch>
            <Condition condition={thread?.purpose === 'applicant_feedback'}>
              Set the reviewers for this position. You can add someone from your team and they will be able to review applicants for this position.
            </Condition>

            <Condition condition={Boolean(thread)}>
              Add or remove participants to this group chat. Make sure at least 2 contacts are selected.
            </Condition>

            <Condition condition>
              Choose the participants for the new group chat from your contact list. Make sure at least 2 contacts are selected.
            </Condition>
          </ConditionSwitch>
        </DialogContentText>

        <TextField
          placeholder="Search"
          name="name"
          value={nameFilter}
          onChange={updateNameFilter}
          fullWidth
          autoFocus={!isMobile()}
          startAdornment={<Search style={{ color: 'rgba(0, 0, 0, 0.54)' }} />}
          data-testid="search"
        />

        <VirtualizedList
          rowComponent={({ index, style }: any) => (
            <Contact
              contactFragmentRef={list[index]}
              style={style}
              disabled={(preselectedContact?.id && list[index].id === preselectedContact.id) || false}
              selected={Boolean(selectedUsersIds.find(id => id === list[index].id))}
              onClick={() => toggleUser(list[index].id)}
            >
              <Zoom in={Boolean(selectedUsersIds.find(id => id === list[index].id))}>
                <Check color="primary" />
              </Zoom>
            </Contact>
          )}
          count={list.length}
          rowHeight={72}
          height={72 * 3}
          noRowsRenderer={() => (
            <ListItem>
              <ListItemIcon><Info /></ListItemIcon>
              <ListItemText
                primary="No Results"
              />
            </ListItem>
          )}
          preselectedUser={preselectedContact}
          selectedUsersIds={selectedUsersIds}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" onClick={save} disabled={disabled}>{thread ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}

export default memo(ChatThreadEditDialog)
